import React, { Fragment, useState } from 'react';
import PropTypes, { object } from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Table,
  ProgressBar,
  DateTime,
  QBRSentiment,
  TablePerson,
  HealthPoint,
  ButtonWithDropdown
} from 'components/common/Elements/Elements';
import Hooks from '../Hooks/Hooks';
import Account from '../Account/Account';
import OneOrGroup from '../OneOrGroup/OneOrGroup';
import Cell from '../Cell/Cell';

import { moneyFormater, underlineToSpace } from 'utils/common';

import classes from '../../AllTenantsTable.module.scss';
import { useRightSideBar, showNotif } from '@p81-common/ui';
import { getAssociationFlow } from '../../../../../../Customer/AssociateTenants/helpers/getAssociationFlow';
import { renderAssociationDrawer } from '../../../../../../Customer/AssociateTenants/helpers/renderAssociationDrawer';
import TenantDetails from '../../../../../../Customer/TenantDetails/TenantDetails';
import BodyDropdownSetting from '../../../../../../common/Elements/ButtonWithDropdown/parts/BodyDropdownSetting/BodyDropdownSetting';
import DeactivateTenant from '../../../../../../Customer/DeactivateTenant/DeactivateTenant';
import SetPAYGTenant from '../../../../../../Customer/SetPAYG/SetPAYGTenant';
import { hasAccess, resources, access } from 'utils/permissions';

const TableRow = ({
  company,
  family,
  noLast,
  row,
  mouseEnter,
  isOtherGrands = false,
  tableConf,
  requestForFilteredData
}) => {
  const [rowHover, setRowHover] = useState(false);
  const { t } = useTranslation(['activity']);
  const { setOpenSecondary, setComponentSecondaryDrawer } = useRightSideBar();
  const showTenantActionsMenu = hasAccess(
    [resources.CUSTOMERS_ASSOCIATION, access.WRITE],
    [resources.CUSTOMERS_DEACTIVATE, access.WRITE]
  );

  const health = {
    point: company.healthPoint,
    status: company.healthStatus
  };

  const onClickTenantDetailsButton = () => {
    setOpenSecondary(true);
    setRowHover(false);
    setComponentSecondaryDrawer(
      <TenantDetails
        company={{ ...company, tenantId: company.workspace.split('.')[0] }}
      />
    );
  };

  const onClickAssociateButton = () => {
    setRowHover(false);
    const flow = getAssociationFlow(company.customerType);
    if (flow === 'unknown') {
      showNotif(
        t('tenantsAssociation.errors.UNKNOWN_TYPE', {
          companyName: company.companyName
        }),
        { alertProps: { severity: 'error' } }
      );
      return;
    }
    setOpenSecondary(true);
    setComponentSecondaryDrawer(
      renderAssociationDrawer(flow, {
        companyName: company.companyName,
        tenantId: company.workspace.split('.')[0],
        customerId: company.customerId,
        updateDataFn: requestForFilteredData
      })
    );
  };

  const onClickDeactivateButton = () => {
    setOpenSecondary(true);
    setRowHover(false);

    setComponentSecondaryDrawer(
      <DeactivateTenant
        companyName={company.companyName}
        tenantId={company.workspace.split('.')[0]}
        updateDataFn={requestForFilteredData}
      />
    );
  };

  const onClickSetPAYGButton = () => {
    setOpenSecondary(true);
    setRowHover(false);

    setComponentSecondaryDrawer(
      <SetPAYGTenant
        companyName={company.companyName}
        customerId={company.customerId}
        updateDataFn={requestForFilteredData}
      />
    );
  };

  const disableAssociateButton =
    company.tenantUUID && company.tenantUUID !== 'NA';

  const disableDeactivateButton =
    company.status !== 'Cancelled' || company.isTerminated;

  const dropdownItems = [
    {
      handleClick: disableAssociateButton
        ? onClickTenantDetailsButton
        : onClickAssociateButton,
      label: disableAssociateButton
        ? t('activity:tenantDetails.BUTTON')
        : t('activity:tenantsAssociation.ASSOCIATE'),
      hide: !hasAccess([resources.CUSTOMERS_ASSOCIATION, access.WRITE])
    },
    {
      isDisabled: disableDeactivateButton,
      handleClick: disableDeactivateButton ? null : onClickDeactivateButton,
      label: company.isTerminated
        ? t('activity:tenantDeactivation.DEACTIVATED')
        : t('activity:tenantDeactivation.DEACTIVATE'),
      hide: !hasAccess([resources.CUSTOMERS_DEACTIVATE, access.WRITE])
    },
    {
      isDisabled: !disableAssociateButton,
      handleClick: disableAssociateButton ? onClickSetPAYGButton : null,
      label: company.isTerminated
        ? t('activity:setTenantPAYG.DEACTIVATED')
        : t('activity:setTenantPAYG.ENABLE_PAYG'),
      hide: !hasAccess([resources.CUSTOMERS_ASSOCIATION, access.WRITE])
    }
  ];

  return (
    <>
      <Table.Row
        className={cn(
          classes.row,
          (family === 'parent' || family === 'child-parent') &&
            classes.tableCell_parent
        )}
        onMouseEnter={() => setRowHover(true)}
        onMouseLeave={() => setRowHover(false)}
      >
        {tableConf.map(({ title, show, field }, ind) => {
          const cellProps = {
            ind: ind,
            field: field,
            row: row,
            mouseEnter: mouseEnter
          };
          return (
            <Fragment key={`${title}-${ind}`}>
              {show && field === 'companyName' && (
                <Cell
                  cellClass={cn(
                    (family === 'child' || family === 'child-parent') &&
                      classes.tableCell_child,
                    family === 'grandson' && classes.tableCell_grandson
                  )}
                  cellProps={cellProps}
                >
                  <Hooks
                    family={family}
                    grands={isOtherGrands}
                    noLast={noLast}
                  />
                  <Account company={company} />
                </Cell>
              )}
              {show && field === 'terminationStatusDate' && (
                <Cell cellProps={cellProps}>
                  {company.terminationStatusDate ? (
                    <DateTime date={company.terminationStatusDate} />
                  ) : (
                    'NA'
                  )}
                </Cell>
              )}
              {show && field === 'healthPoint' && (
                <Cell cellProps={cellProps}>
                  <HealthPoint data={health} />
                </Cell>
              )}
              {show && field === 'tmUtilized' && (
                <Cell cellProps={cellProps}>
                  <ProgressBar
                    is={company.tmUtilized}
                    of={company.tmLicenses}
                  />
                </Cell>
              )}
              {show && field === 'psUtilized' && (
                <Cell cellProps={cellProps}>
                  {`${company.psUtilized}/${company.psLicenses}`}
                </Cell>
              )}
              {show && field === 'plan' && (
                <Cell cellProps={cellProps}>
                  <OneOrGroup
                    text={t('activity:customers:TOTAL_PLANS')}
                    data={company.plan}
                  />
                </Cell>
              )}
              {show && field === 'billingCycle' && (
                <Cell cellProps={cellProps}>
                  <OneOrGroup
                    text={t('activity:customers:TOTAL_CYCLES')}
                    data={company.billingCycle}
                  />
                </Cell>
              )}
              {show && field === 'arr' && (
                <Cell
                  cellClass={cn(
                    (family === 'parent' || family === 'child-parent') &&
                      classes.tableCell_parent_bold
                  )}
                  cellProps={cellProps}
                >
                  {moneyFormater(company.arr)}
                </Cell>
              )}
              {show && field === 'mau' && (
                <Cell cellProps={cellProps}>
                  {`${company.mau || '0'}/${company.tmUtilized}`}
                </Cell>
              )}
              {show && field === 'npsScore' && (
                <Cell cellProps={cellProps}>
                  {company.npsScore ? company.npsScore : 'NA'}
                </Cell>
              )}
              {show && field === 'openTickets' && (
                <Cell cellProps={cellProps}>{company.openTickets}</Cell>
              )}
              {show && field === 'csatChatScore' && (
                <Cell cellProps={cellProps}>{company.csatChatScore}</Cell>
              )}
              {show && field === 'csatTicketScore' && (
                <Cell cellProps={cellProps}>{company.csatTicketScore}</Cell>
              )}
              {show && field === 'firstPaymentDate' && (
                <Cell
                  cellClass={cn(
                    (family === 'parent' || family === 'child-parent') &&
                      classes.tableCell_parent_bold
                  )}
                  cellProps={cellProps}
                >
                  <DateTime date={company.firstPaymentDate} />
                </Cell>
              )}
              {show && field === 'firstInvoiceAt' && (
                <Cell
                  cellClass={cn(
                    (family === 'parent' || family === 'child-parent') &&
                      classes.tableCell_parent_bold
                  )}
                  cellProps={cellProps}
                >
                  <DateTime date={company.firstInvoiceAt} />
                </Cell>
              )}
              {show && field === 'nextBillingDate' && (
                <Cell
                  cellClass={cn(
                    (family === 'parent' || family === 'child-parent') &&
                      classes.tableCell_parent_bold
                  )}
                  cellProps={cellProps}
                >
                  <DateTime date={company.nextBillingDate} />
                </Cell>
              )}
              {show && field === 'industry' && (
                <Cell cellProps={cellProps}>{company.industry}</Cell>
              )}
              {show && field === 'source' && (
                <Cell cellProps={cellProps}>{company.source}</Cell>
              )}
              {show && field === 'accountManager' && (
                <Cell cellProps={cellProps} classNameContent={classes.center}>
                  {company.accountManager !==
                  t('activity:customers:UNASSIGNED') ? (
                    <TablePerson person={company.accountManager} />
                  ) : (
                    t('activity:customers:UNASSIGNED')
                  )}
                </Cell>
              )}
              {show && field === 'customerSuccessEngineer' && (
                <Cell cellProps={cellProps} classNameContent={classes.center}>
                  {company.customerSuccessEngineer !==
                  t('activity:customers:UNASSIGNED') ? (
                    <TablePerson person={company.customerSuccessEngineer} />
                  ) : (
                    t('activity:customers:UNASSIGNED')
                  )}
                </Cell>
              )}
              {show && field === 'appsUtilized' && (
                <Cell cellProps={cellProps}>
                  {`${company.appsUtilized}/${company.appsLicenses}`}
                </Cell>
              )}
              {show && field === 'ageInMonths' && (
                <Cell cellProps={cellProps}>{company.ageInMonths}</Cell>
              )}
              {show && field === 'region' && (
                <Cell cellProps={cellProps}>{company.region}</Cell>
              )}
              {show && field === 'coupons.name' && (
                <Cell cellProps={cellProps}>
                  {company.coupons && company.coupons.name
                    ? company.coupons.name
                    : 'NA'}
                </Cell>
              )}
              {show && field === 'coupons.type' && (
                <Cell cellProps={cellProps}>
                  {company.coupons && company.coupons.name
                    ? company.coupons.type
                    : 'NA'}
                </Cell>
              )}
              {show && field === 'coupons.duration' && (
                <Cell cellProps={cellProps}>
                  {company.coupons && company.coupons.name
                    ? company.coupons.duration
                    : 'NA'}
                </Cell>
              )}
              {show && field === 'paymentType' && (
                <Cell cellProps={cellProps}>
                  {company.paymentType === 'paypal_express_checkout'
                    ? 'PayPal'
                    : underlineToSpace(company.paymentType)}
                </Cell>
              )}
              {show && field === 'qbr.sentiment' && (
                <Cell cellProps={cellProps}>
                  <QBRSentiment
                    sentiment={company.qbr && company.qbr.sentiment}
                  />
                </Cell>
              )}
              {show && field === 'qbr.occurredDate' && (
                <Cell
                  cellClass={cn(
                    (family === 'parent' || family === 'child-parent') &&
                      classes.tableCell_parent_bold
                  )}
                  cellProps={cellProps}
                >
                  {company.qbr && company.qbr.occurredDate ? (
                    <DateTime date={company.qbr.occurredDate} />
                  ) : (
                    'NA'
                  )}
                </Cell>
              )}
            </Fragment>
          );
        })}
      </Table.Row>
      {showTenantActionsMenu && rowHover && (
        <div
          className={classes.hoverCell}
          onMouseEnter={() => setRowHover(true)}
          onMouseLeave={() => setRowHover(false)}
        >
          <ButtonWithDropdown
            tooltipDisabled
            icon="3dots"
            degRotateImg={90}
            position="left"
            color={'#42526D'}
            closeOnChange={true}
            buttonClass={classes.associateButton}
          >
            <BodyDropdownSetting items={dropdownItems} />
          </ButtonWithDropdown>
        </div>
      )}
    </>
  );
};

TableRow.propTypes = {
  company: PropTypes.object,
  family: PropTypes.string,
  noLast: PropTypes.bool,
  row: PropTypes.string,
  mouseEnter: PropTypes.func,
  isOtherGrands: PropTypes.bool,
  tableConf: PropTypes.arrayOf(object),
  requestForFilteredData: PropTypes.func
};

export default TableRow;
